/**
 * Annotation Service
 * Handles fetching and mapping data to PDF annotations
 */

import axios from 'axios';
import { getTableAnnotation, getMediaAnnotation, getPricingClassesAnnotation, getSurchagesAnnotation, getOutOfWeightAnnotation, getWarmToColdWeightAnnotation, getLiveWeightToPaidWeightAnnotation, getNuchterheidsindexAnnotation } from '../config/annotationData';

/**
 * Fetches annotation data from the backend webservice
 * This data is typically the result of a calculation or data processing service
 * 
 * @param {string} desecnm - Document section number
 * @param {string} defvsnr - Document version number
 * @param {string} factuurvoorstelnummer - Invoice proposal number
 * @returns {Promise<Object>} The annotation data response
 * @throws {Error} Throws error on 403 or if mock data is disabled
 */
export const fetchAnnotationData = async (desecnm, defvsnr, factuurvoorstelnummer) => {
  const useMockData = process.env.REACT_APP_USE_MOCK_ANNOTATION_DATA === 'true';

  try {
    const response = await axios.get('/jsonapi/document_details_afrekeningsborderel', {
      params: {
        facvstnr: factuurvoorstelnummer,
      },
    });

    return response.data;
  } catch (err) {
    // If 403 Forbidden, throw error (user doesn't have access)
    if (err.response && err.response.status === 403) {
      console.warn('Annotation Service: Access denied (403) - annotations will not be displayed');
      throw new Error('FORBIDDEN'); // Throw specific error to be caught at component level
    }

    // Only use mock data if explicitly enabled in environment
    if (useMockData) {
      console.warn('Using mock annotation data (REACT_APP_USE_MOCK_ANNOTATION_DATA=true)');
      // DEVELOPMENT FALLBACK: Use mock data when enabled
      const mockData = {
      "klassement": [
        {
          "250": "MOCK-6",
          "251": "MOCK-7",
          "252": "MOCK-671,50",
          "253": "MOCK-658,20",
          "254": "MOCK-5",
          "255": "MOCK-1.053,22",
          "256": "MOCK-1,6002",
          "271": "MOCK-6",
          "272": "MOCK-5",
          "273": "MOCK-0",
          "289": "MOCK-0",
          "290": "MOCK-2",
          "274": "MOCK-7",
          "275": "MOCK-5",
          "276": "MOCK-658,2",
          "277": "MOCK-6,7",
          "278": "MOCK-14,4",
          "279": "MOCK-66,0",
          "280": "MOCK-7,1",
          "281": "MOCK-11,1",
          "282": "MOCK-69,0",
          "283": "MOCK-63,4",
          "284": "MOCK-35",
          "285": "MOCK-3,26",
          "286": "MOCK-0,0337",
          "287": "MOCK-0,0300",
          "288": "MOCK-1,6002"
        },
        {
          "250": "MOCK-7",
          "251": "MOCK-27",
          "252": "MOCK-2.664,80",
          "253": "MOCK-2.611,40",
          "254": "MOCK-18",
          "255": "MOCK-4.081,02",
          "256": "MOCK-1,5628",
          "271": "MOCK-7",
          "272": "MOCK-18",
          "273": "MOCK-0",
          "289": "MOCK-0",
          "290": "MOCK-9",
          "274": "MOCK-27",
          "275": "MOCK-18",
          "276": "MOCK-2611,4",
          "277": "MOCK-6,1",
          "278": "MOCK-15,2",
          "279": "MOCK-63,8",
          "280": "MOCK-8,1",
          "281": "MOCK-12,5",
          "282": "MOCK-66,6",
          "283": "MOCK-62,1",
          "284": "MOCK-37",
          "285": "MOCK-3,59",
          "286": "MOCK-0,0162",
          "287": "MOCK-0,0262",
          "288": "MOCK-1,5628"
        },
        {
          "250": "MOCK-8",
          "251": "MOCK-54",
          "252": "MOCK-5.246,00",
          "253": "MOCK-5.141,20",
          "254": "MOCK-36",
          "255": "MOCK-7.968,99",
          "256": "MOCK-1,5500",
          "271": "MOCK-8",
          "272": "MOCK-36",
          "273": "MOCK-0",
          "289": "MOCK-0",
          "290": "MOCK-18",
          "274": "MOCK-54",
          "275": "MOCK-36",
          "276": "MOCK-5141,2",
          "277": "MOCK-7,2",
          "278": "MOCK-17,1",
          "279": "MOCK-62,4",
          "280": "MOCK-9,0",
          "281": "MOCK-13,2",
          "282": "MOCK-64,2",
          "283": "MOCK-61,1",
          "284": "MOCK-39",
          "285": "MOCK-3,91",
          "286": "MOCK-0,0209",
          "287": "MOCK-0,0282",
          "288": "MOCK-1,5500"
        },
        {
          "250": "MOCK-9",
          "251": "MOCK-35",
          "252": "MOCK-3.409,00",
          "253": "MOCK-3.340,80",
          "254": "MOCK-23",
          "255": "MOCK-5.096,47",
          "256": "MOCK-1,5255",
          "271": "MOCK-9",
          "272": "MOCK-12",
          "273": "MOCK-0",
          "289": "MOCK-0",
          "290": "MOCK-23",
          "274": "MOCK-35",
          "275": "MOCK-23",
          "276": "MOCK-3340,8",
          "277": "MOCK-8,2",
          "278": "MOCK-19,3",
          "279": "MOCK-62,3",
          "280": "MOCK-10,4",
          "281": "MOCK-14,5",
          "282": "MOCK-64,0",
          "283": "MOCK-59,8",
          "284": "MOCK-41",
          "285": "MOCK-4,24",
          "286": "MOCK-0,0263",
          "287": "MOCK-0,0284",
          "288": "MOCK-1,5255"
        },
        {
          "250": "MOCK-10",
          "251": "MOCK-21",
          "252": "MOCK-2.040,70",
          "253": "MOCK-2.000,00",
          "254": "MOCK-14",
          "255": "MOCK-2.909,61",
          "256": "MOCK-1,4548",
          "271": "MOCK-10",
          "272": "MOCK-6",
          "273": "MOCK-0",
          "289": "MOCK-0",
          "290": "MOCK-15",
          "274": "MOCK-21",
          "275": "MOCK-14",
          "276": "MOCK-2000,0",
          "277": "MOCK-9,5",
          "278": "MOCK-21,1",
          "279": "MOCK-61,8",
          "280": "MOCK-11,7",
          "281": "MOCK-15,8",
          "282": "MOCK-63,0",
          "283": "MOCK-58,6",
          "284": "MOCK-43",
          "285": "MOCK-4,53",
          "286": "MOCK-0,0239",
          "287": "MOCK-0,0000",
          "288": "MOCK-1,4548"
        },
        {
          "250": "MOCK-11",
          "251": "MOCK-3",
          "252": "MOCK-283,50",
          "253": "MOCK-277,80",
          "254": "MOCK-2",
          "255": "MOCK-393,46",
          "256": "MOCK-1,4163",
          "271": "MOCK-11",
          "272": "MOCK-3",
          "273": "MOCK-0",
          "289": "MOCK-0",
          "290": "MOCK-0",
          "274": "MOCK-3",
          "275": "MOCK-2",
          "276": "MOCK-277,8",
          "277": "MOCK-12,3",
          "278": "MOCK-26,8",
          "279": "MOCK-62,9",
          "280": "MOCK-14,9",
          "281": "MOCK-19,0",
          "282": "MOCK-64,4",
          "283": "MOCK-55,9",
          "284": "MOCK-48",
          "285": "MOCK-4,85",
          "286": "MOCK-0,0242",
          "287": "MOCK-0,0000",
          "288": "MOCK-1,4163"
        },
        {
          "250": "MOCK-12",
          "251": "MOCK-3",
          "252": "MOCK-312,70",
          "253": "MOCK-306,50",
          "254": "MOCK-2",
          "255": "MOCK-410,88",
          "256": "MOCK-1,3406",
          "271": "MOCK-12",
          "272": "MOCK-1",
          "273": "MOCK-0",
          "289": "MOCK-0",
          "290": "MOCK-2",
          "274": "MOCK-3",
          "275": "MOCK-2",
          "276": "MOCK-306,5",
          "277": "MOCK-13,0",
          "278": "MOCK-26,1",
          "279": "MOCK-61,7",
          "280": "MOCK-14,8",
          "281": "MOCK-18,6",
          "282": "MOCK-62,1",
          "283": "MOCK-55,7",
          "284": "MOCK-48",
          "285": "MOCK-5,09",
          "286": "MOCK-0,0024",
          "287": "MOCK-0,0000",
          "288": "MOCK-1,3406"
        }
      ],
      "klassement_totalen": {
        "232": "MOCK-81",
        "233": "MOCK-0",
        "261": "MOCK-0",
        "262": "MOCK-69",
        "234": "MOCK-150",
        "236": "MOCK-95,6",
        "237": "MOCK-7,8",
        "238": "MOCK-18,1",
        "239": "MOCK-62,7",
        "240": "MOCK-9,7",
        "241": "MOCK-13,9",
        "242": "MOCK-64,6",
        "243": "MOCK-60,5",
        "244": "MOCK-40",
        "245": "MOCK-4,03",
        "246": "MOCK-0,0220",
        "247": "MOCK-0,0229",
        "248": "MOCK-1,5286",
        "300": "MOCK-150",
        "301": "MOCK-14.628,20",
        "305": "MOCK-1,245800"
      },
      "Autofomgroep": [
        {
          "Index": "MOCK-12",
          "Aantal": 3
        },
        {
          "Index": "MOCK-7",
          "Aantal": 27
        },
        {
          "Index": "MOCK-9",
          "Aantal": 34
        },
        {
          "Index": "MOCK-10",
          "Aantal": 21
        },
        {
          "Index": "MOCK-8",
          "Aantal": 54
        },
        {
          "Index": "MOCK-6",
          "Aantal": 7
        },
        {
          "Index": "MOCK-11",
          "Aantal": 4
        }
      ],
      "Koud geslacht gewicht": [
        {
          "Gewicht": "MOCK-115-125",
          "Aantal": 2
        },
        {
          "Gewicht": "MOCK-100-105",
          "Aantal": 29
        },
        {
          "Gewicht": "MOCK-105-115",
          "Aantal": 6
        },
        {
          "Gewicht": "MOCK-65-80",
          "Aantal": 2
        },
        {
          "Gewicht": "MOCK-80-88",
          "Aantal": 13
        },
        {
          "Gewicht": "MOCK-88-100",
          "Aantal": 98
        }
      ],
      "Verdeling gewichtscategorie": [
        {
          "Koud Geslacht Gewicht": "MOCK-115-120",
          "% Aantal": 1.3333333333333333,
          "AFI \/ MBI": 3.6451785
        },
        {
          "Koud Geslacht Gewicht": "MOCK-85-90",
          "% Aantal": 13.333333333333334,
          "AFI \/ MBI": 3.972006
        },
        {
          "Koud Geslacht Gewicht": "MOCK-80-85",
          "% Aantal": 2.6666666666666665,
          "AFI \/ MBI": 3.987351
        },
        {
          "Koud Geslacht Gewicht": "MOCK-105-110",
          "% Aantal": 3.3333333333333335,
          "AFI \/ MBI": 4.2357308
        },
        {
          "Koud Geslacht Gewicht": "MOCK-75-80",
          "% Aantal": 1.3333333333333333,
          "AFI \/ MBI": 4.093453
        },
        {
          "Koud Geslacht Gewicht": "MOCK-100-105",
          "% Aantal": 19.333333333333332,
          "AFI \/ MBI": 4.07592151724138
        },
        {
          "Koud Geslacht Gewicht": "MOCK-95-100",
          "% Aantal": 26.666666666666668,
          "AFI \/ MBI": 4.042070375
        },
        {
          "Koud Geslacht Gewicht": "MOCK-90-95",
          "% Aantal": 31.333333333333332,
          "AFI \/ MBI": 4.008342170212766
        },
        {
          "Koud Geslacht Gewicht": "MOCK-110-115",
          "% Aantal": 0.6666666666666666,
          "AFI \/ MBI": 4.064528
        }
      ]
    };
    
      return mockData;
    }

    // If mock data is not enabled and we reach here, return null (no annotations)
    console.warn('Annotation Service: Failed to fetch data and mock data is disabled - annotations will not be displayed');
    return null;
  }
};

/**
 * Creates annotations from the first klassement object and table data
 * 
 * @param {Object} data - The raw response from the webservice
 * @returns {Array<Object>} Array containing annotation objects
 */
export const mapDataToAnnotations = async (data) => {
  const annotations = [];

  if (!data || !data.klassement || data.klassement.length === 0) {
    return [];
  }

  // Get only the first klassement object
  const firstKlassement = data.klassement[0];

  // Create annotation for classification data
  const klassementAnnotation = {
    id: 'klassement-first',
    number: 1,
    x: 2, // Position on left side of PDF
    y: 19, // Position at 19% of PDF height
    text: 'Click to view details',
    title: 'Classification Details',
    annotationType: 'data',
    page: 1,
    visible: true,
    // Store the raw data so we can display it completely in the panel
    data: firstKlassement,
    metadata: {
      klassement_totalen: data.klassement_totalen,
    },
  };

    annotations.push(klassementAnnotation);

    // Add pricing classes table annotation
    const pricingClassesAnnotation = getPricingClassesAnnotation();
    annotations.push(pricingClassesAnnotation);

    // Add table annotation with hardcoded measurement parameters
    const tableAnnotation = getTableAnnotation();
    annotations.push(tableAnnotation);

    // Add media annotation with image and text
    const mediaAnnotation = getMediaAnnotation();
    annotations.push(mediaAnnotation);

    // Add surcharges table annotation
    const surchagesAnnotation = getSurchagesAnnotation();
    annotations.push(surchagesAnnotation);

    // Add out of weight table annotation
    const outOfWeightAnnotation = getOutOfWeightAnnotation();
    annotations.push(outOfWeightAnnotation);

    // Add warm to cold weight annotation
    const warmToColdWeightAnnotation = getWarmToColdWeightAnnotation();
    annotations.push(warmToColdWeightAnnotation);

    // Add live weight to paid weight annotation
    const liveWeightToPaidWeightAnnotation = getLiveWeightToPaidWeightAnnotation();
    annotations.push(liveWeightToPaidWeightAnnotation);

    // Add nuchterheidsindex annotation
    const nuchterheidsindexAnnotation = getNuchterheidsindexAnnotation();
    annotations.push(nuchterheidsindexAnnotation);

    return annotations;
};

/**
 * Main function to fetch and map annotations in one call
 * 
 * @param {string} desecnm - Document section number
 * @param {string} defvsnr - Document version number
 * @param {string} factuurvoorstelnummer - Invoice proposal number
 * @returns {Promise<Array<Object>>} Array containing annotation objects
 */
export const fetchAndMapAnnotations = async (desecnm, defvsnr, factuurvoorstelnummer) => {
  try {
    const data = await fetchAnnotationData(desecnm, defvsnr, factuurvoorstelnummer);
    return await mapDataToAnnotations(data);
  } catch (err) {
    // If it's a FORBIDDEN error, re-throw so component knows not to show any annotations
    if (err.message === 'FORBIDDEN') {
      throw err;
    }
    // For other errors, return empty array
    return [];
  }
};

export default {
  fetchAnnotationData,
  mapDataToAnnotations,
  fetchAndMapAnnotations,
};
